import { metersBetween } from './geo.js';

// Generic entity interpolation primitives, shared by every moving-vehicle layer
// (buses, ferries, …). Each layer tweens its markers from where they currently
// are to the latest reported position over a glide window kept a hair longer than
// that feed's poll cadence, so a marker is essentially always mid-glide when the
// next report lands and never freezes waiting for it. No timestamp dependence, no
// velocity estimation, no teleporting. (See busMotion.ts / ferryMotion.ts for the
// per-feed tuning and the rationale behind keeping the window close to the cadence.)

/** An in-progress glide: traverse `from` → `to` over `durationMs` starting at
 * `startMs` (wall-clock ms from performance.now). */
export interface EntityGlide {
  from: [number, number];
  to: [number, number];
  startMs: number;
  durationMs: number;
}

/** Linear interpolation between two [lng,lat] points; `frac` is clamped to 0..1. */
export function lerp(from: [number, number], to: [number, number], frac: number): [number, number] {
  const f = frac <= 0 ? 0 : frac >= 1 ? 1 : frac;
  return [from[0] + (to[0] - from[0]) * f, from[1] + (to[1] - from[1]) * f];
}

/** Where the marker should be drawn right now for an in-progress glide. */
export function glidePosition(g: EntityGlide, nowMs: number): [number, number] {
  if (g.durationMs <= 0) return g.to;
  return lerp(g.from, g.to, (nowMs - g.startMs) / g.durationMs);
}

/** Start a fresh glide from `from` toward `to`, lasting one (over-long) glide window. */
export function startGlide(
  from: [number, number],
  to: [number, number],
  startMs: number,
  durationMs: number,
): EntityGlide {
  return { from, to, startMs, durationMs };
}

/** A glide that holds a single point (first sight, or a snap/teleport). */
export function settledGlide(
  at: [number, number],
  startMs: number,
  durationMs: number,
): EntityGlide {
  return { from: at, to: at, startMs, durationMs };
}

export { metersBetween };
